import React, { useState } from 'react';
import { Select, DatePicker, Modal } from '../UI';
import { fmt, OTHER_OPTION, withOtherOption, withOtherOptionFirst, otherAwareId, otherAwareText, otherAwareValue } from '../../utils';
import { useUOM } from '../../hooks/useUOM';

// ─────────────────────────────────────────────────────────────
// The single line-items grid shared by every requisition form
// except HR (which keeps its own employee-based layout).
// Adopted from the Administration & Corporate Services design —
// change it here and every department form changes with it.
// ─────────────────────────────────────────────────────────────

export const gridSelectStyle = {
  width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: 8,
  border: '1px solid var(--border-light)', borderRadius: 6, fontSize: 12.5,
  background: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
export const gridSelectDisabledStyle = { ...gridSelectStyle, background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed' };

// Plain <input> boxes in the grid. Same border, radius and type size as the
// pickers they sit between — left to the browser's default input chrome they
// render as a heavier, squarer box, which made the numeric columns look like
// a different kind of control from everything around them.
export const gridInputStyle = {
  width: '100%', boxSizing: 'border-box', padding: 8,
  border: '1px solid var(--border-light)', borderRadius: 6, fontSize: 12.5,
  background: '#fff',
};

// The derived figures — a line's cost is calculated, never typed. Same box as
// the fields around it so the row reads as one set of columns, but centred and
// bold: it is the number the row exists to produce, not another input.
export const gridDerivedStyle = {
  ...gridInputStyle, display: 'block', textAlign: 'center', fontWeight: 800,
};

// Read-only "last purchase" reference boxes — orange-based, solid border.
export const lastPurchaseBoxStyle = {
  display: 'block', padding: '7px 8px', borderRadius: 6,
  border: '1px solid #f97316', background: '#fff7ed', color: '#c2410c',
  fontWeight: 700, textAlign: 'center', fontSize: 12.5,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

// Kept as a static fallback only — the component uses the DB-driven useUOM hook instead.
export const UNIT_OPTIONS = [
  'pcs', 'nos', 'mtrs', 'kgs', 'ltrs', 'set', 'lot', 'roll',
  'bag', 'sheet', 'service', 'session', 'ream', 'packs', 'cartons',
];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'critical', label: 'Critical' },
];

/** Blank line in the shared shape — every form starts rows from here. */
export const blankLineItem = (today) => ({
  project_id: '', material_id: '', material_other: '', description: '', specification: '',
  vendor_id: '', vendor_other: '', boq_item_id: '', boq_component_id: '', expense_category_id: '', expense_category_other: '',
  brand: '', unit_of_measure: 'pcs', quantity: 1, estimated_unit_rate: 0, vat_percentage: 0,
  cost_centre_id: '', account_ledger_id: '', ledger_id: '', ledger_other: '',
  expense_sub_category_id: '',
  cost_object_type_id: '', category_asset_type_id: '', additional_info: '',
  logistics_additional_info: [],
  item_id: null,
  req_date: today || '', priority_status: 'normal', remarks: '',
  last_date: null, last_qty: null, last_rate: null, last_total: null,
  last_vendor_id: '', last_vendor_name: '',
  item_requested_for_user_id: null, item_requested_for_name: null,
});

/** Build a line from a "Select From Previous Purchases" history row. The
 *  last_* fields are a frozen snapshot of what was bought before — shown
 *  read-only beside the editable figures for comparison. */
// `requireVendor`: the requisition's "Require a Vendor?" toggle. Off, the copied line does not
// carry the old vendor in (it stays visible as last_vendor_* for comparison).
export const lineItemFromHistory = (h, today, { requireVendor = true } = {}) => ({
  ...blankLineItem(today),
  material_id: h.material_id || '',
  description: h.description || '',
  specification: h.specification || '',
  vendor_id: requireVendor ? h.vendor_id || '' : '',
  project_id: h.project_id || '',
  brand: h.brand || '',
  unit_of_measure: h.unit_of_measure || 'pcs',
  quantity: h.quantity || 1,
  estimated_unit_rate: h.estimated_unit_rate || 0,
  account_ledger_id: h.account_ledger_id || '',
  last_date: h.pr_date || null,
  last_qty: h.quantity || null,
  last_rate: h.estimated_unit_rate || null,
  last_total: h.estimated_amount || null,
  last_vendor_id: h.vendor_id || '',
  last_vendor_name: h.vendor_name || '',
});

export const computeItemAmount = (item) =>
  (parseFloat(item.quantity) || 0) * (parseFloat(item.estimated_unit_rate) || 0);

// VAT on one line. Every form derives it this way and the server derives it
// the same way from the rate it is sent, so the figure on screen and the
// figure stored are the same number rather than two that happen to agree.
// Rounded to the kobo per line, exactly as the server rounds it.
export const computeItemVat = (item) =>
  Math.round(computeItemAmount(item) * ((parseFloat(item.vat_percentage) || 0) / 100) * 100) / 100;

/** Requisition totals: the lines, the VAT they carry, and the payable total.
 *  Shared so every form — and the server — add a requisition up identically.
 *  The adjustment is NOT taxed: it is a manual correction with no rate of its
 *  own, so it joins the total untaxed. */
export const computeRequisitionTotals = (items, adjustmentsAmount, amountOf = computeItemAmount) => {
  const gross = (items || []).reduce((sum, it) => sum + amountOf(it), 0);
  const vat = (items || []).reduce(
    (sum, it) => sum + Math.round(amountOf(it) * ((parseFloat(it.vat_percentage) || 0) / 100) * 100) / 100,
    0,
  );
  const adjustments = parseFloat(adjustmentsAmount) || 0;
  return { gross, vat, total: gross + adjustments + vat };
};

/** Shared payload mapping so both forms send an identical item shape. With the requisition's
 *  "Require a Vendor?" toggle off (`requireVendor: false`) no line vendor is sent. The options
 *  object (not a bare boolean) keeps `items.map(mapLineItemForApi)` safe: map's index is ignored. */
export const mapLineItemForApi = (item, { requireVendor = true } = {}) => ({
  // Echoed back so the server can update this line IN PLACE. Without it every
  // edit deleted the row and rebuilt it, which threw away Cost Control's and
  // the approver's review figures. Absent on a new line, which is exactly what
  // "this is new" means to the server.
  item_id: item.item_id || null,
  project_id: item.project_id || null,
  material_id: otherAwareId(item.material_id),
  material_other: otherAwareText(item.material_id, item.material_other),
  description: item.description,
  specification: item.specification || null,
  quantity: item.quantity || 1,
  unit_of_measure: item.unit_of_measure || 'pcs',
  estimated_unit_rate: item.estimated_unit_rate || null,
  estimated_amount: computeItemAmount(item),
  vat_percentage: parseFloat(item.vat_percentage) || 0,
  vat_amount: computeItemVat(item),
  boq_item_id: item.boq_item_id || null,
  boq_component_id: item.boq_component_id || null,
  vendor_id: requireVendor ? otherAwareId(item.vendor_id) : null,
  vendor_other: requireVendor ? otherAwareText(item.vendor_id, item.vendor_other) : null,
  priority_status: item.priority_status || 'normal',
  req_date: item.req_date || null,
  brand: item.brand || null,
  account_ledger_id: item.account_ledger_id || null,
  expense_category_id: otherAwareId(item.expense_category_id),
  expense_category_other: otherAwareText(item.expense_category_id, item.expense_category_other),
  // Only meaningful under a real category — an "Others" line has no children.
  expense_sub_category_id: (item.expense_category_id && item.expense_category_id !== OTHER_OPTION)
    ? (item.expense_sub_category_id || null)
    : null,
  cost_centre_id: item.cost_centre_id || null,
  cost_object_type_id: item.cost_object_type_id || null,
  category_asset_type_id: item.category_asset_type_id || null,
  additional_info: item.additional_info || null,
  logistics_additional_info: item.logistics_additional_info || [],
  remarks: item.remarks || null,
  ledger_id: otherAwareId(item.ledger_id),
  ledger_other: otherAwareText(item.ledger_id, item.ledger_other),
  last_date: item.last_date || null,
  last_qty: item.last_qty || null,
  last_rate: item.last_rate || null,
  last_total: item.last_total || null,
  last_vendor_id: item.last_vendor_id || null,
  item_requested_for_user_id: item.item_requested_for_user_id === OTHER_OPTION ? null : (item.item_requested_for_user_id || null),
  item_requested_for_name: item.item_requested_for_user_id === OTHER_OPTION ? (item.item_requested_for_name || null) : null,
  remarks: item.remarks || null,
});

/** Map an API item back into the shared editing shape. */
export const lineItemFromApi = (item) => ({
  // Carried through the form untouched so it can be sent back on save.
  item_id: item.item_id || null,
  material_id: otherAwareValue(item.material_id, item.material_other),
  material_other: item.material_other || '',
  description: item.description || '',
  specification: item.specification || '',
  vendor_id: otherAwareValue(item.vendor_id, item.vendor_other),
  vendor_other: item.vendor_other || '',
  project_id: item.project_id || '',
  boq_item_id: item.boq_item_id || '',
  boq_component_id: item.boq_component_id || '',
  expense_category_id: otherAwareValue(item.expense_category_id, item.expense_category_other),
  expense_category_other: item.expense_category_other || '',
  expense_sub_category_id: item.expense_sub_category_id || '',
  brand: item.brand || '',
  unit_of_measure: item.unit_of_measure || 'pcs',
  quantity: item.quantity || 1,
  estimated_unit_rate: item.estimated_unit_rate || 0,
  vat_percentage: item.vat_percentage != null ? parseFloat(item.vat_percentage) : 0,
  cost_centre_id: item.cost_centre_id || '',
  account_ledger_id: item.account_ledger_id || '',
  ledger_id: otherAwareValue(item.ledger_id, item.ledger_other),
  ledger_other: item.ledger_other || '',
  cost_object_type_id: item.cost_object_type_id || '',
  category_asset_type_id: item.category_asset_type_id || '',
  additional_info: item.additional_info || '',
  req_date: item.req_date || '',
  priority_status: item.priority_status || 'normal',
  remarks: item.remarks || '',
  last_date: item.last_date || null,
  last_qty: item.last_qty || null,
  last_rate: item.last_rate || null,
  last_total: item.last_total || null,
  last_vendor_id: item.last_vendor_id || '',
  last_vendor_name: item.last_vendor_name || '',
  item_requested_for_user_id: item.item_requested_for_user_id
    ? item.item_requested_for_user_id
    : (item.item_requested_for_name ? OTHER_OPTION : null),
  item_requested_for_name: item.item_requested_for_name || null,
});

/** Remove-this-line control. Shared so the two requisition grids cannot end
 *  up offering the same action under different icons. */
export const RemoveLineButton = ({ onClick, title = 'Remove line' }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    style={{
      background: 'none', border: 'none', color: '#ef4444',
      cursor: 'pointer', padding: 4, lineHeight: 0,
    }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  </button>
);

/** A line's VAT, for the review grids.
 *
 *  One column carrying both facts a reviewer needs: the money it adds, and the
 *  rate it came from — a ₦375 that is 7.5% of the line reads very differently
 *  from a ₦375 that is 5%. Lines carrying no VAT show a dash rather than
 *  ₦0.00, so a taxable line stands out at a glance down the column.
 */
export const vatCell = (item) => {
  const amount = Number(item?.vat_amount ?? 0);
  const rate = Number(item?.vat_percentage ?? 0);
  if (!amount && !rate) return '—';
  return (
    <span>
      {/* fmt() already carries the naira sign — see utils.fmt. The grids that
          write "₦{fmt(x)}" have their OWN local fmt that does not. */}
      {fmt(amount)}
      {rate ? (
        <span style={{ display: 'block', fontSize: 10, color: '#64748b', marginTop: 1 }}>
          {rate}%
        </span>
      ) : null}
    </span>
  );
};

const th = (minWidth) => ({ padding: '10px', minWidth });

// A red border for a field the validator flagged — but only while it is still
// empty, so filling it in clears the border immediately without waiting for
// another save. Deriving it from the current value rather than clearing the
// flag on change means it also clears when a row is filled programmatically,
// as "Select From Previous Purchases" does.
const errorBorder = '1.5px solid #ef4444';
const flagged = (errors, key, value) => !!errors[key] && !value;

// ─────────────────────────────────────────────────────────────
// LogisticsInfoModal — local-state supplementary fuel/vehicle
// details. No separate API: rows are saved with the PR form.
// ─────────────────────────────────────────────────────────────
const EMPTY_ROW = {
  product: '', vehicle_number: '', last_top_up_date: '', gasoline_station: '',
  litres_requested: '', curr_price_per_litre: '', total_amount: '',
  prev_mileage: '', curr_mileage: '', last_price_per_litre: '', last_litres: '',
  others: '', remarks: '',
};

const LAI_COLS = [
  { key: 'product', label: 'PRODUCT' },
  { key: 'vehicle_number', label: 'VEHICLE NO.' },
  { key: 'last_top_up_date', label: 'LAST TOP-UP', type: 'date' },
  { key: 'gasoline_station', label: 'GASOLINE STATION' },
  { key: 'litres_requested', label: 'LITRES REQ.', type: 'number' },
  { key: 'curr_price_per_litre', label: 'CURR PRICE/L', type: 'number' },
  { key: 'total_amount', label: 'TOTAL AMT', type: 'number' },
  { key: 'prev_mileage', label: 'PREV MILEAGE', type: 'number' },
  { key: 'curr_mileage', label: 'CURR MILEAGE', type: 'number' },
  { key: 'last_price_per_litre', label: 'LAST PRICE/L', type: 'number' },
  { key: 'last_litres', label: 'LAST LITRES', type: 'number' },
  { key: 'others', label: 'OTHERS' },
  { key: 'remarks', label: 'REMARKS' },
];

function LogisticsInfoModal({ rows, onChange, onClose }) {
  const [draft, setDraft] = useState(null);
  const [draftData, setDraftData] = useState(null);
  const [confirmIdx, setConfirmIdx] = useState(null);

  const openNew = () => { setDraft(-1); setDraftData({ ...EMPTY_ROW }); };
  const openEdit = (i) => { setDraft(i); setDraftData({ ...rows[i] }); };
  const cancel = () => { setDraft(null); setDraftData(null); };

  const save = () => {
    const updated = rows ? [...rows] : [];
    const cleaned = Object.fromEntries(
      Object.entries(draftData).map(([k, v]) => [k, v === '' ? null : v])
    );
    if (draft === -1) { updated.push(cleaned); }
    else { updated[draft] = { ...updated[draft], ...cleaned }; }
    onChange(updated);
    cancel();
  };

  const confirmRemove = (i) => setConfirmIdx(i);
  const remove = () => {
    onChange((rows || []).filter((_, idx) => idx !== confirmIdx));
    setConfirmIdx(null);
  };

  const inp = (field) => ({
    value: draftData?.[field] ?? '',
    onChange: (e) => setDraftData(d => ({ ...d, [field]: e.target.value })),
    style: { width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 },
  });

  const totalLitres = (rows || []).reduce((s, r) => s + (parseFloat(r.litres_requested) || 0), 0);
  const totalFuel   = (rows || []).reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0);

  const thS = { padding: '8px 6px', background: '#0b318b', color: '#fff', fontSize: 11.5, whiteSpace: 'nowrap', textAlign: 'center', border: '1px solid #cbd5e1' };
  const tdS = { padding: '6px 8px', fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap', border: '1px solid #e5e7eb' };
  const btnPrimary   = { padding: '7px 18px', background: '#0b318b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
  const btnSecondary = { padding: '7px 18px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 };

  return (
    <>
      {/* Inline confirmation — uses the same Modal shell at higher z-index */}
      {confirmIdx !== null && (
        <Modal title="Remove Row?" onClose={() => setConfirmIdx(null)} width={380}>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px' }}>
            This row will be removed when you save the requisition.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmIdx(null)} style={btnSecondary}>Cancel</button>
            <button onClick={remove} style={{ ...btnPrimary, background: '#dc2626' }}>Remove</button>
          </div>
        </Modal>
      )}

      <Modal title="Logistics Additional Information" onClose={onClose} width={1300}>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>
          Supplementary vehicle, fuel &amp; logistics details — saved with the requisition
        </p>

        {/* Summary cards */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total Rows',     val: (rows || []).length },
            { label: 'Total Litres',   val: fmt(totalLitres) },
            { label: 'Total Fuel Cost', val: '₦' + fmt(totalFuel) },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, background: '#f0f4ff', borderRadius: 8, padding: '10px 16px',
              textAlign: 'center', border: '1px solid black',
            }}>
              <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#0b318b', marginTop: 2 }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Rows table */}
        <div style={{ overflowX: 'auto', marginBottom: 14, border: '1px solid black', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ ...thS, width: 36 }}>#</th>
                {LAI_COLS.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
                <th style={{ ...thS, width: 90 }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={tdS}>{i + 1}</td>
                  {LAI_COLS.map(c => (
                    <td key={c.key} style={tdS}>
                      {c.type === 'number' ? fmt(r[c.key]) : (r[c.key] || '—')}
                    </td>
                  ))}
                  <td style={tdS}>
                    <button onClick={() => openEdit(i)} style={{ ...btnSecondary, padding: '3px 10px', fontSize: 11, marginRight: 4 }}>Edit</button>
                    <button onClick={() => confirmRemove(i)} style={{ padding: '3px 10px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Del</button>
                  </td>
                </tr>
              ))}
              {!(rows || []).length && (
                <tr><td colSpan={LAI_COLS.length + 2} style={{ ...tdS, color: '#9ca3af', padding: 20 }}>No rows yet. Click "+ Add Row" to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Inline add / edit form */}
        {draft !== null && (
          <div style={{ background: '#f0f4ff', borderRadius: 8, padding: 16, marginBottom: 14, border: '1px solid black' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0b318b', marginBottom: 10 }}>
              {draft === -1 ? 'New Row' : `Edit Row ${draft + 1}`}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
              {LAI_COLS.map(c => (
                <div key={c.key}>
                  <label style={{ display: 'block', fontSize: 11, color: '#374151', fontWeight: 600, marginBottom: 3 }}>{c.label}</label>
                  <input type={c.type === 'number' ? 'number' : c.type === 'date' ? 'date' : 'text'} step={c.type === 'number' ? 'any' : undefined} {...inp(c.key)} />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button onClick={save} style={btnPrimary}>Save Row</button>
              <button onClick={cancel} style={btnSecondary}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {draft === null && <button onClick={openNew} style={btnPrimary}>+ Add Row</button>}
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </Modal>
    </>
  );
}

export default function RequisitionLineItemsTable({
  items,
  updateItem,
  removeItem,
  errors = {},
  materials = [],
  suppliers = [],
  projects = [],
  boqDataByProject = {},
  expenseCategories = [],
  expenseSubCategories = [],
  costCentres = [],
  accountLedgers = [],
  ledgers = [],
  logisticsMode = false,
  costObjectTypes = [],
  categoryAssetTypes = [],
  users = [],
  requireVendor = false,
}) {
  const [infoModalIdx, setInfoModalIdx] = useState(null);
  const { uomOptions } = useUOM();

  return (
    <>
    {infoModalIdx !== null && (
      <LogisticsInfoModal
        rows={items[infoModalIdx]?.logistics_additional_info || []}
        onChange={(newRows) => updateItem(infoModalIdx, { logistics_additional_info: newRows })}
        onClose={() => setInfoModalIdx(null)}
      />
    )}
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: logisticsMode ? 3060 : 2330 }}>
        <thead>
          <tr style={{ background: '#0b318bff', color: '#fff' }}>
            <th style={{ padding: '10px' }}>#</th>
            <th style={th(160)}>MATERIAL</th>
            <th style={th(170)}>DESCRIPTION</th>
            <th style={th(120)}>SPEC</th>
            <th style={th(160)}>REQUESTED FOR</th>
            <th style={th(150)}>VENDOR</th>
            <th style={th(150)}>PROJECT</th>
            {logisticsMode && <th style={th(140)}>COST OBJECT TYPE</th>}
            {logisticsMode && <th style={th(140)}>CATEGORY / ASSET TYPE</th>}
            <th style={th(130)}>BOQ</th>
            <th style={th(130)}>SUB BOQ</th>
            <th style={th(170)}>EXPENSE CATEGORY</th>
            <th style={th(170)}>SUB EXPENSE CATEGORY</th>
            <th style={th(100)}>BRAND</th>
            <th style={th(90)}>UNIT</th>
            <th style={th(70)}>QTY</th>
            <th style={th(100)}>RATE</th>
            <th style={th(110)}>EST. COST</th>
            <th style={th(90)}>VAT (%)</th>
            <th style={th(130)}>COST CENTRE</th>
            <th style={th(150)}>{logisticsMode ? 'ELC LEDGER' : 'GL ACCOUNT'}</th>
            <th style={th(140)}>{logisticsMode ? 'SUB ELC' : 'LEDGER'}</th>
            <th style={th(130)}>REQ. DATE</th>
            <th style={th(100)}>PRIORITY</th>
            <th style={th(120)}>REMARKS</th>
            {logisticsMode && <th style={th(160)}>ADDITIONAL INFO</th>}
            <th style={th(100)}>LAST DATE</th>
            <th style={th(80)}>LAST QTY</th>
            <th style={th(90)}>LAST RATE</th>
            <th style={th(100)}>LAST TOTAL</th>
            <th style={th(130)}>LAST VENDOR</th>
            <th style={{ padding: '10px' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const rowBoqs = boqDataByProject[item.project_id] || [];
            const selectedBoq = rowBoqs.find((b) => String(b.boq_item_id || b.id) === String(item.boq_item_id));
            const subBoqOptions = selectedBoq?.components || [];
            const amount = computeItemAmount(item);
            // Children of the category on THIS row. "Others" is free text and
            // has no children, so it resolves to an empty list.
            const subOptions = (item.expense_category_id && item.expense_category_id !== OTHER_OPTION)
              ? expenseSubCategories
                  .filter((sc) => String(sc.expense_category_id) === String(item.expense_category_id))
                  .map((sc) => ({ value: sc.expense_sub_category_id, label: `${sc.code} · ${sc.name}` }))
              : [];
            const hasHistory = !!item.last_date;

            return (
              <tr key={idx} style={{ borderBottom: '1px solid #eee', background: hasHistory ? '#f0fdf4' : '#fff' }}>
                <td style={{ padding: 8, textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Material"
                    style={flagged(errors, `material_id_${idx}`, item.material_id) ? { ...gridSelectStyle, border: errorBorder } : gridSelectStyle}
                    value={item.material_id || ''}
                    onChange={(v) => updateItem(idx, { material_id: v })}
                    options={withOtherOptionFirst(materials.map((m) => ({ value: m.id || m.material_id, label: m.name })))}
                    placeholder="Select"
                  />
                  {item.material_id === OTHER_OPTION && (
                    <input
                      style={{ ...gridSelectStyle, marginTop: 6 }}
                      placeholder="Specify material *"
                      value={item.material_other || ''}
                      onChange={(e) => updateItem(idx, { material_other: e.target.value })}
                    />
                  )}
                </td>
                <td style={{ padding: 8 }}>
                  <input
                    style={flagged(errors, `description_${idx}`, item.description)
                      ? { ...gridInputStyle, border: errorBorder } : gridInputStyle}
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(idx, { description: e.target.value })}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input style={gridInputStyle} placeholder="Spec" value={item.specification} onChange={(e) => updateItem(idx, { specification: e.target.value })} />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Requested For"
                    style={gridSelectStyle}
                    value={item.item_requested_for_user_id || ''}
                    onChange={(v) => updateItem(idx, {
                      item_requested_for_user_id: v || null,
                      item_requested_for_name: v === OTHER_OPTION ? (item.item_requested_for_name || '') : null,
                    })}
                    options={[
                      { value: '', label: '— None —' },
                      { value: OTHER_OPTION, label: 'Other (type name)' },
                      ...users.map((u) => ({ value: u.user_id || u.id, label: `${u.first_name} ${u.last_name}` })),
                    ]}
                    placeholder="— None —"
                  />
                  {item.item_requested_for_user_id === OTHER_OPTION && (
                    <input
                      style={{ ...gridSelectStyle, marginTop: 6 }}
                      placeholder="Type full name *"
                      value={item.item_requested_for_name || ''}
                      onChange={(e) => updateItem(idx, { item_requested_for_name: e.target.value })}
                    />
                  )}
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Vendor"
                    style={requireVendor ? gridSelectStyle : gridSelectDisabledStyle}
                    value={item.vendor_id || ''}
                    onChange={(v) => { if (requireVendor) updateItem(idx, { vendor_id: v }); }}
                    options={requireVendor ? withOtherOption(suppliers.map((s) => ({ value: s.id || s.supplier_id, label: s.name }))) : []}
                    placeholder={requireVendor ? 'Select' : '—'}
                    disabled={!requireVendor}
                  />
                  {requireVendor && item.vendor_id === OTHER_OPTION && (
                    <input
                      style={{ ...gridSelectStyle, marginTop: 6 }}
                      placeholder="Specify vendor *"
                      value={item.vendor_other || ''}
                      onChange={(e) => updateItem(idx, { vendor_other: e.target.value })}
                    />
                  )}
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Project"
                    style={flagged(errors, `project_id_${idx}`, item.project_id) ? { ...gridSelectStyle, border: errorBorder } : gridSelectStyle}
                    value={item.project_id || ''}
                    onChange={(v) => updateItem(idx, { project_id: v })}
                    options={projects.map((p) => ({ value: p.id || p.project_id, label: p.project_name || p.name }))}
                  />
                </td>
                {logisticsMode && (
                  <td style={{ padding: 8 }}>
                    <Select
                      style={gridSelectStyle}
                      value={item.cost_object_type_id || ''}
                      onChange={(v) => updateItem(idx, { cost_object_type_id: v || '', category_asset_type_id: '' })}
                      options={[
                        { value: '', label: '— Select —' },
                        ...costObjectTypes.map((c) => ({ value: c.id, label: c.name })),
                      ]}
                    />
                  </td>
                )}
                {logisticsMode && (
                  <td style={{ padding: 8 }}>
                    <Select
                      style={item.cost_object_type_id ? gridSelectStyle : gridSelectDisabledStyle}
                      value={item.category_asset_type_id || ''}
                      onChange={(v) => updateItem(idx, { category_asset_type_id: v || '' })}
                      disabled={!item.cost_object_type_id}
                      options={[
                        { value: '', label: item.cost_object_type_id ? '— Select —' : '— Pick type first —' },
                        ...categoryAssetTypes
                          .filter((c) => !item.cost_object_type_id || c.cost_object_type_id === item.cost_object_type_id)
                          .map((c) => ({ value: c.id, label: c.name })),
                      ]}
                    />
                  </td>
                )}
                <td style={{ padding: 8 }}>
                  <Select
                    title="BOQ Item"
                    style={!item.project_id ? gridSelectDisabledStyle : gridSelectStyle}
                    value={item.boq_item_id || ''}
                    onChange={(v) => updateItem(idx, { boq_item_id: v, boq_component_id: '' })}
                    disabled={!item.project_id}
                    options={rowBoqs.map((b) => ({ value: b.boq_item_id || b.id, label: `${b.boq_code || ''} — ${b.description}` }))}
                    placeholder="Select BOQ"
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Sub BOQ"
                    style={(!item.boq_item_id || subBoqOptions.length === 0) ? gridSelectDisabledStyle : gridSelectStyle}
                    value={item.boq_component_id || ''}
                    onChange={(v) => updateItem(idx, { boq_component_id: v })}
                    disabled={!item.boq_item_id || subBoqOptions.length === 0}
                    options={subBoqOptions.map((c) => ({ value: c.component_id || c.id, label: c.description || c.name }))}
                    placeholder="Select Sub BOQ"
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Expense Category"
                    style={gridSelectStyle}
                    value={item.expense_category_id || ''}
                    // Clearing the child is not optional: left behind, the line
                    // would carry a sub category belonging to a category it is
                    // no longer under.
                    onChange={(v) => updateItem(idx, { expense_category_id: v, expense_sub_category_id: '' })}
                    options={withOtherOption(expenseCategories.map((e2) => ({ value: e2.id || e2.expense_category_id, label: `${e2.code} · ${e2.name}` })))}
                  />
                  {/* Sits under the picker rather than in its own column, so
                      choosing "Others" does not shift every column to its
                      right for every other row in the grid. */}
                  {item.expense_category_id === OTHER_OPTION && (
                    <input
                      style={{ ...gridSelectStyle, marginTop: 6 }}
                      placeholder="Specify category *"
                      value={item.expense_category_other || ''}
                      onChange={(e) => updateItem(idx, { expense_category_other: e.target.value })}
                    />
                  )}
                </td>
                {/* Driven entirely by the category chosen to its left. Most
                    categories have no children, so this is disabled far more
                    often than not — the placeholder says which case you are
                    looking at rather than leaving an empty box to puzzle over. */}
                <td style={{ padding: 8 }}>
                  <Select
                    title="Sub Expense Category"
                    style={subOptions.length ? gridSelectStyle : gridSelectDisabledStyle}
                    disabled={!subOptions.length}
                    value={item.expense_sub_category_id || ''}
                    onChange={(v) => updateItem(idx, { expense_sub_category_id: v })}
                    options={[{ value: '', label: '— None —' }, ...subOptions]}
                    placeholder={
                      !item.expense_category_id || item.expense_category_id === OTHER_OPTION
                        ? 'Pick a category first'
                        : 'No sub categories'
                    }
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input style={gridInputStyle} placeholder="Brand" value={item.brand} onChange={(e) => updateItem(idx, { brand: e.target.value })} />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Unit of Measure"
                    style={gridSelectStyle}
                    value={item.unit_of_measure || 'pcs'}
                    onChange={(v) => updateItem(idx, { unit_of_measure: v })}
                    options={uomOptions}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input type="number" style={{ ...gridInputStyle, width: 70 }} value={item.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                </td>
                <td style={{ padding: 8 }}>
                  <input type="number" style={{ ...gridInputStyle, width: 90 }} value={item.estimated_unit_rate} onChange={(e) => updateItem(idx, { estimated_unit_rate: e.target.value })} />
                </td>
                <td style={{ padding: 8 }}>
                  <span style={gridDerivedStyle}>{fmt(amount)}</span>
                </td>
                {/* Per-line VAT: 0 by default, because a requisition can carry
                    taxable and non-taxable lines side by side. */}
                <td style={{ padding: 8 }}>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    style={{ ...gridInputStyle, width: 70 }}
                    value={item.vat_percentage ?? 0}
                    onChange={(e) => updateItem(idx, { vat_percentage: e.target.value })}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Cost Centre"
                    style={gridSelectStyle}
                    value={item.cost_centre_id || ''}
                    onChange={(v) => updateItem(idx, { cost_centre_id: v })}
                    options={costCentres.map((c) => ({ value: c.id, label: c.name }))}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Account Ledger"
                    style={gridSelectStyle}
                    value={item.account_ledger_id || ''}
                    onChange={(v) => updateItem(idx, { account_ledger_id: v })}
                    options={accountLedgers.map((a) => ({ value: a.ledger_id, label: `${a.code} · ${a.name}` }))}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Ledger"
                    style={gridSelectStyle}
                    value={item.ledger_id || ''}
                    onChange={(v) => updateItem(idx, { ledger_id: v })}
                    options={withOtherOption(ledgers.map((l) => ({ value: l.id, label: l.name })))}
                  />
                  {item.ledger_id === OTHER_OPTION && (
                    <input
                      style={{ ...gridSelectStyle, marginTop: 6 }}
                      placeholder="Specify ledger *"
                      value={item.ledger_other || ''}
                      onChange={(e) => updateItem(idx, { ledger_other: e.target.value })}
                    />
                  )}
                </td>
                <td style={{ padding: 8 }}>
                  <DatePicker
                    value={item.req_date || ''}
                    onChange={(v) => updateItem(idx, { req_date: v })}
                    style={{ padding: '7.5px', fontSize: 12.5, border: '1px solid #cbd5e1', width: '100%' }}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Select
                    title="Priority"
                    style={gridSelectStyle}
                    value={item.priority_status || 'normal'}
                    onChange={(v) => updateItem(idx, { priority_status: v })}
                    options={PRIORITY_OPTIONS}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <input style={gridInputStyle} placeholder="Remarks" value={item.remarks} onChange={(e) => updateItem(idx, { remarks: e.target.value })} />
                </td>
                {logisticsMode && (
                  <td style={{ padding: 8, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => setInfoModalIdx(idx)}
                      title="View / add logistics additional info"
                      style={{
                        padding: '5px 14px', background: '#0b318b', color: '#fff',
                        border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        position: 'relative',
                      }}
                    >
                      Info
                      {(item.logistics_additional_info || []).length > 0 && (
                        <span style={{
                          position: 'absolute', top: -6, right: -6, background: '#ef4444',
                          color: '#fff', borderRadius: '50%', width: 16, height: 16,
                          fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                        }}>
                          {(item.logistics_additional_info || []).length}
                        </span>
                      )}
                    </button>
                  </td>
                )}
                <td style={{ padding: 8 }}><span style={lastPurchaseBoxStyle}>{item.last_date || '—'}</span></td>
                <td style={{ padding: 8 }}><span style={lastPurchaseBoxStyle}>{item.last_qty != null ? fmt(item.last_qty) : '—'}</span></td>
                <td style={{ padding: 8 }}><span style={lastPurchaseBoxStyle}>{item.last_rate != null ? fmt(item.last_rate) : '—'}</span></td>
                <td style={{ padding: 8 }}><span style={lastPurchaseBoxStyle}>{item.last_total != null ? fmt(item.last_total) : '—'}</span></td>
                <td style={{ padding: 8 }}><span style={lastPurchaseBoxStyle}>{item.last_vendor_name || '—'}</span></td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  {items.length > 1 && (
                    <RemoveLineButton onClick={() => removeItem(idx)} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}
